import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, MapPin, Calendar, Plane, X, Info, TrendingDown, Clock, Filter, ChevronDown, CalendarIcon } from 'lucide-react';
import AirportInput from '../components/search/AirportInput';
import PassengerSelector from '../components/search/PassengerSelector';
import CabinClassSelector from '../components/search/CabinClassSelector';
import { searchGoogleTravelExplore } from '@/api/functions';
import { format, parse, parseISO } from 'date-fns';
import { buildFlightSearchParams, prefetchFlightSearch } from '@/lib/searchPrefetch';
import { initialSearchTravelers } from '@/lib/usualTravelers';
import { useSearchPickers } from '@/hooks/useSearchPickers';
import { checkBuildWhileSearching, openSearchResults } from '@/lib/searchNavigation';
import { normalizeCabinClass } from '@/utils/cabinClass';
import { isSeatedInfant, writePassengerParams } from '@/utils/passengerTypes';
import { nextSearchDateRange } from '@/utils/searchDateRange';
import { ErrorBoundary } from '@/lib/errorLogger';
import FlexibleDateSelector from '../components/explore/FlexibleDateSelector';
import DestinationPreviewOverlay from '../components/explore/DestinationPreviewOverlay';
import AllInPriceToggle from '@/components/ui/AllInPriceToggle';
import { useAllInPrices } from '@/hooks/useAllInPrices';
import { useInstallmentsCheckoutProvider } from '@/hooks/useInstallmentsCheckoutProvider';
import { allInTotalFromFare, estimatedDepositFromFare } from '@/utils';
import ExploreMap from '../components/explore/ExploreMap';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import SectionBand from '@/components/ui/SectionBand';
import {
  BRAND_LIST_DIVIDER,
  BRAND_PRIMARY_BTN_CLASS,
  BRAND_SELECTED_TOGGLE_CLASS,
  CART_SECTION_CARD,
  DASHBOARD_FILTER_INPUT,
  DASHBOARD_TAB_INACTIVE,
  REWARDS_SECTION_CARD,
} from '@/constants/brandColors';
import { cn } from '@/lib/utils';
import PageSeo from '@/components/PageSeo';

const exploreToggleClass = (active) =>
  cn(
    'flex-1 px-3 py-1.5 rounded-full font-normal text-sm transition-all border',
    active ? BRAND_SELECTED_TOGGLE_CLASS : DASHBOARD_TAB_INACTIVE,
  );

const exploreFieldClass = cn(DASHBOARD_FILTER_INPUT, 'rounded-full');

/** Lead days from a YYYY-MM-DD start date, for the deposit-tier estimate. */
const exploreLeadDays = (startDate) => {
  const t = startDate ? Date.parse(startDate) : NaN;
  return Number.isFinite(t) ? Math.floor((t - Date.now()) / 86400000) : null;
};

const exploreLabelClass = 'text-[11px] text-white/45 uppercase tracking-[0.12em] font-normal mb-2 block';

/** An explore result's date ("2027-01-12T00:00:00" or "2027-01-12 00:00") as the
 *  yyyy-MM-dd day the results page's URL carries, or null. */
const exploreDay = (value) => (value ? String(value).split(/[T ]/)[0] : null);

export default function Explore() {
  // Same price-display preference as search/booking options (default all-in).
  const [showAllInPrices, setShowAllInPrices] = useAllInPrices();
  const installmentsCheckoutProvider = useInstallmentsCheckoutProvider();
  const navigate = useNavigate();
  const location = useLocation();
  const [origin, setOrigin] = useState('');
  const [destinations, setDestinations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [componentError, setComponentError] = useState(null);

  // Debug: Log when component mounts
  useEffect(() => {
    console.log('Explore component mounted');
  }, []);

  // Error boundary for component errors
  useEffect(() => {
    const errorHandler = (event) => {
      console.error('Explore component error:', event.error);
      setComponentError(event.error?.message || 'An error occurred');
    };
    window.addEventListener('error', errorHandler);
    return () => window.removeEventListener('error', errorHandler);
  }, []);
  
  // Flexible date parameters
  const [month, setMonth] = useState('0'); // 0 = all months, 1-12 = specific month
  const [travelDuration, setTravelDuration] = useState('2'); // 1 = Weekend, 2 = 1 week, 3 = 2 weeks
  const [dateMode, setDateMode] = useState('flexible'); // 'flexible' or 'specific'
  const [specificDates, setSpecificDates] = useState({ from: undefined, to: undefined });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  
  // Trip type
  const [tripType, setTripType] = useState('1'); // '1' = Round trip, '2' = One way
  
  // Passengers: the party this customer usually searches for (usualTravelers.js).
  const [passengers, setPassengers] = useState(initialSearchTravelers);
  const { isTravelersOpen, onTravelersOpenChange, earlySearchDelay } = useSearchPickers();
  const [cabinClass, setCabinClass] = useState('economy');
  
  // Map state
  const [mapCenter, setMapCenter] = useState({ lat: 39.8283, lng: -98.5795 }); // Center of USA
  const [mapZoom, setMapZoom] = useState(4);
  const [mapBounds, setMapBounds] = useState(null); // Current visible map bounds
  
  // Filter state - Cities by default
  const [filterType, setFilterType] = useState('cities'); // 'cities' or 'destinations'
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const filterMenuRef = useRef(null);

  // Close filter menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
        setShowFilterMenu(false);
      }
    };

    if (showFilterMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterMenu]);

  // Track if auto-search has been triggered
  const autoSearchTriggeredRef = useRef(false);
  const hasSearchedRef = useRef(false);

  // Load origin from URL params or localStorage
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlOrigin = urlParams.get('origin');
    if (urlOrigin) {
      setOrigin(urlOrigin);
    } else {
      // Try to get from localStorage or default
      const savedOrigin = localStorage.getItem('lastSearchOrigin');
      if (savedOrigin) {
        setOrigin(savedOrigin);
      }
    }
    // Reset auto-search flag when origin changes
    autoSearchTriggeredRef.current = false;
    hasSearchedRef.current = false;
  }, [location.search]);

  // Auto-trigger search when origin is set
  useEffect(() => {
    if (origin && !autoSearchTriggeredRef.current && !isLoading && !hasSearchedRef.current) {
      autoSearchTriggeredRef.current = true;
      hasSearchedRef.current = true;
      // Small delay to ensure state is set
      const timer = setTimeout(() => {
        handleSearch();
      }, 200);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, isLoading]);

  const handleSearch = useCallback(async () => {
    if (!origin) {
      setError('Please select a departure airport');
      return;
    }

    setIsLoading(true);
    setError(null);
    setDestinations([]);
    setSelectedDestination(null);
    setShowPreview(false);
    hasSearchedRef.current = true;

    try {
      // Save origin to localStorage
      localStorage.setItem('lastSearchOrigin', origin);

      // Debug: Log the current tripType state before validation
      console.log('[Explore] Current tripType state:', tripType, typeof tripType);
      console.log('[Explore] Date mode:', dateMode);
      console.log('[Explore] Specific dates:', specificDates);

      // Validate tripType - ensure it's either '1' or '2'
      // Convert to string and ensure it's valid
      const validatedTripType = String(tripType) === '2' ? '2' : '1';
      if (validatedTripType !== String(tripType)) {
        console.warn('[Explore] tripType mismatch - state:', tripType, 'validated:', validatedTripType);
      }

      console.log('[Explore] Using tripType:', validatedTripType, 'for API request');

      // Build search parameters according to SerpAPI docs
      // Ensure tripType is correctly set: '1' = Round trip, '2' = One way
      const searchParams = {
        departure_id: origin.toUpperCase(),
        type: validatedTripType, // '1' = Round trip, '2' = One way
        travel_class: cabinClass === 'economy' ? '1' : cabinClass === 'premium_economy' ? '2' : cabinClass === 'business' ? '3' : '4',
        adults: String(passengers.filter(p => p.type === 'adult').length || 1),
        children: String(passengers.filter(p => p.type === 'child').length || 0),
        infants_in_seat: String(passengers.filter(p => isSeatedInfant(p.type)).length || 0),
        infants_on_lap: String(passengers.filter(p => p.type === 'infant_without_seat').length || 0),
        currency: 'USD',
        gl: 'us',
        hl: 'en'
      };

      // Add specific dates if in specific date mode
      // CRITICAL: travel_duration and month cannot be used with outbound_date/return_date
      if (dateMode === 'specific' && specificDates.from) {
        searchParams.outbound_date = format(specificDates.from, 'yyyy-MM-dd');
        // Only add return_date for round trips (type === '1')
        // For one-way trips (type === '2'), NEVER include return_date
        if (validatedTripType === '1' && specificDates.to) {
          searchParams.return_date = format(specificDates.to, 'yyyy-MM-dd');
        } else if (validatedTripType === '2') {
          // Explicitly remove return_date for one-way trips
          delete searchParams.return_date;
        }
        // Explicitly ensure travel_duration and month are NOT included
        // These are mutually exclusive with outbound_date/return_date
        delete searchParams.travel_duration;
        delete searchParams.month;
      } else {
        // For flexible dates, add month and travel_duration
        // Explicitly ensure outbound_date and return_date are NOT included
        delete searchParams.outbound_date;
        delete searchParams.return_date;
        if (month !== '0') {
          searchParams.month = month;
        }
        searchParams.travel_duration = travelDuration; // 1 = Weekend, 2 = 1 week, 3 = 2 weeks
      }

      // Final safety check: ensure no conflicting parameters
      if (searchParams.outbound_date) {
        delete searchParams.travel_duration;
        delete searchParams.month;
      }
      if (searchParams.travel_duration) {
        delete searchParams.outbound_date;
        delete searchParams.return_date;
      }

      // CRITICAL: For one-way trips (type === '2'), ensure return_date is NEVER included
      if (searchParams.type === '2') {
        delete searchParams.return_date;
      }

      // CRITICAL: For round trips (type === '1') with specific dates, return_date should be present
      // But don't fail if it's missing - let SerpAPI handle that validation
      
      console.log('Sending search params:', {
        type: searchParams.type,
        tripType: tripType,
        outbound_date: searchParams.outbound_date,
        return_date: searchParams.return_date,
        travel_duration: searchParams.travel_duration,
        month: searchParams.month,
        allParams: Object.keys(searchParams).reduce((acc, key) => {
          acc[key] = searchParams[key];
          return acc;
        }, {})
      });

      const { data, status } = await searchGoogleTravelExplore(searchParams);

      if (status !== 200 || data.error) {
        throw new Error(data.error || 'Failed to search destinations');
      }

      if (data.destinations && data.destinations.length > 0) {
        // Calculate minimum allowed date (21 days from today)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const minBookingDate = new Date(today);
        minBookingDate.setDate(today.getDate() + 21);

        // Filter out destinations with departure dates less than 21 days away
        const validDestinations = data.destinations.filter(dest => {
          if (dest.start_date) {
            try {
              const startDateStr = dest.start_date.includes('T') 
                ? dest.start_date.split('T')[0] 
                : dest.start_date.split(' ')[0];
              const startDate = new Date(startDateStr);
              startDate.setHours(0, 0, 0, 0);
              
              if (startDate < minBookingDate) {
                return false; // Skip destinations with dates too soon
              }
            } catch (error) {
              console.warn('Error parsing start_date:', dest.start_date, error);
              // If we can't parse the date, exclude it to be safe
              return false;
            }
          }
          return true;
        });

        setDestinations(validDestinations);
        
        // Only center map on initial load (when destinations array was empty)
        // Don't recenter when adding new region destinations to prevent glitching
        if (validDestinations.length > 0 && destinations.length === 0 && validDestinations[0]?.gps_coordinates) {
          setMapCenter({
            lat: validDestinations[0].gps_coordinates.latitude,
            lng: validDestinations[0].gps_coordinates.longitude
          });
          setMapZoom(5);
        } else if (validDestinations.length === 0 && destinations.length === 0) {
          setError('No destinations found with valid dates (must be at least 21 days in advance). Try different search parameters.');
        }
      } else {
        setError('No destinations found. Try different search parameters.');
      }
    } catch (err) {
      console.error('Explore search failed:', err);
      setError(err.message || 'An unexpected error occurred during the search.');
    } finally {
      setIsLoading(false);
    }
  }, [origin, month, travelDuration, dateMode, specificDates, passengers, cabinClass, tripType]);

  const handleDestinationClick = useCallback((destination) => {
    setSelectedDestination(destination);
    setShowPreview(true);
    
    // Center map on destination
    if (destination.gps_coordinates) {
      setMapCenter({
        lat: destination.gps_coordinates.latitude,
        lng: destination.gps_coordinates.longitude
      });
      setMapZoom(8);
    }
  }, []);

  // Handle region change when map is dragged to a new area
  const handleRegionChange = useCallback(async (regionKgmid, bounds) => {
    if (!origin || !regionKgmid) {
      return;
    }

    try {
      setIsLoading(true);
      
      // Build search parameters for the new region
      // Validate tripType - ensure it's either '1' or '2'
      const validatedTripType = String(tripType) === '2' ? '2' : '1';
      const searchParams = {
        departure_id: origin.toUpperCase(),
        arrival_area_id: regionKgmid, // Use arrival_area_id for region-based search
        type: validatedTripType,
        travel_class: cabinClass === 'economy' ? '1' : cabinClass === 'premium_economy' ? '2' : cabinClass === 'business' ? '3' : '4',
        adults: String(passengers.filter(p => p.type === 'adult').length || 1),
        children: String(passengers.filter(p => p.type === 'child').length || 0),
        infants_in_seat: String(passengers.filter(p => isSeatedInfant(p.type)).length || 0),
        infants_on_lap: String(passengers.filter(p => p.type === 'infant_without_seat').length || 0),
        currency: 'USD',
        gl: 'us',
        hl: 'en'
      };

      // Add date parameters based on current mode
      if (dateMode === 'specific' && specificDates.from) {
        searchParams.outbound_date = format(specificDates.from, 'yyyy-MM-dd');
        if (validatedTripType === '1' && specificDates.to) {
          searchParams.return_date = format(specificDates.to, 'yyyy-MM-dd');
        }
        delete searchParams.travel_duration;
        delete searchParams.month;
      } else {
        delete searchParams.outbound_date;
        delete searchParams.return_date;
        if (month !== '0') {
          searchParams.month = month;
        }
        searchParams.travel_duration = travelDuration;
      }

      if (searchParams.type === '2') {
        delete searchParams.return_date;
      }

      // Log the region search to verify correct KGMID format
      console.log('[Explore] Region search:', {
        regionKgmid,
        isValidKGMID: regionKgmid.startsWith('/m/'),
        searchParams: {
          departure_id: searchParams.departure_id,
          arrival_area_id: searchParams.arrival_area_id,
          type: searchParams.type
        }
      });

      const { data, status } = await searchGoogleTravelExplore(searchParams);
      
      // Log only for Asia to debug
      if (regionKgmid === '/m/0j0k') {
        console.log('[Explore] Asia search result:', { status, count: data?.destinations?.length });
      }

      // Handle empty results gracefully - don't treat as error
      if (status === 200 && data && data.destinations) {
        // Even if empty array, continue processing (filtering will handle it)
        if (data.destinations.length === 0) {
          return; // Just return, don't merge empty array
        }
        // Calculate minimum allowed date (21 days from today)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const minBookingDate = new Date(today);
        minBookingDate.setDate(today.getDate() + 21);

        // Filter valid destinations (dates >= 21 days, valid prices)
        const validDestinations = data.destinations.filter(dest => {
          const price = dest.flight_price;
          if (price === null || price === undefined || price === 'N/A' || price === '' || isNaN(parseFloat(price))) {
            return false;
          }

          if (dest.start_date) {
            try {
              const startDateStr = dest.start_date.includes('T') 
                ? dest.start_date.split('T')[0] 
                : dest.start_date.split(' ')[0];
              const startDate = new Date(startDateStr);
              startDate.setHours(0, 0, 0, 0);
              
              if (startDate < minBookingDate) {
                return false;
              }
            } catch (error) {
              return false;
            }
          }
          return true;
        });

        // Merge with existing destinations, avoiding duplicates
        if (validDestinations.length > 0) {
          setDestinations(prev => {
            const existingIds = new Set(prev.map(d => d.destination_id || `${d.gps_coordinates?.latitude}-${d.gps_coordinates?.longitude}`));
            const newDestinations = validDestinations.filter(d => {
              const id = d.destination_id || `${d.gps_coordinates?.latitude}-${d.gps_coordinates?.longitude}`;
              return !existingIds.has(id);
            });
            return [...prev, ...newDestinations];
          });
        }
      }
    } catch (error) {
      console.error('[Explore] Error searching region:', error);
      // Don't show error to user for background region searches - just log it
    } finally {
      setIsLoading(false);
    }
  }, [origin, tripType, cabinClass, passengers, dateMode, specificDates, month, travelDuration]);

  const handleViewFlights = useCallback((destination) => {
    // Navigate to search page with destination pre-filled and trigger search
    const params = new URLSearchParams();
    params.set('origin', origin);
    params.set('destination', destination.destination_airport?.code || destination.name);
    
    // Dates as yyyy-MM-dd (the date portion of the explore result's timestamp)
    const departDate = exploreDay(destination.start_date);
    if (departDate) params.set('departureDate', departDate);
    const returnDate = exploreDay(destination.end_date);
    if (returnDate) params.set('returnDate', returnDate);
    
    writePassengerParams(params, passengers);
    
    params.set('cabin', cabinClass);
    
    openSearchResults(navigate, `/search?${params.toString()}`);
  }, [origin, passengers, cabinClass, navigate]);

  // "View flights" opens the results page for the destination on screen, so
  // that search starts as soon as its preview has been open a moment and the
  // results page takes it over (src/lib/searchPrefetch.js). It is built the
  // way the results page reads handleViewFlights' URL back. Opening a
  // destination also re-checks for a newer Defera, which "View flights"
  // would then load (src/lib/searchNavigation.js).
  useEffect(() => {
    if (!showPreview || !selectedDestination || isTravelersOpen) return undefined;
    checkBuildWhileSearching();
    const departDate = exploreDay(selectedDestination.start_date);
    const returnDate = exploreDay(selectedDestination.end_date);
    const params = buildFlightSearchParams({
      origin,
      destination: selectedDestination.destination_airport?.code,
      from: departDate ? parse(departDate, 'yyyy-MM-dd', new Date()) : undefined,
      to: returnDate ? parse(returnDate, 'yyyy-MM-dd', new Date()) : undefined,
      passengers,
      cabinClass: normalizeCabinClass(cabinClass),
      canonicalPassengers: true,
    });
    if (!params) return undefined;
    const timer = setTimeout(() => prefetchFlightSearch(params), earlySearchDelay());
    return () => clearTimeout(timer);
  }, [showPreview, selectedDestination, origin, passengers, cabinClass, isTravelersOpen, earlySearchDelay]);

  const formatDuration = useCallback((minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    }
    return `${mins}m`;
  }, []);

  // Date selection handler for specific dates
  // With both dates picked, a day before the departure moves it and keeps the
  // return; any other day starts a new trip there (src/utils/searchDateRange.js).
  // `tappedDay` is react-day-picker's second onSelect argument.
  const handleDateSelect = useCallback((range, tappedDay) => {
    const hadBoth = Boolean(specificDates.from && specificDates.to);
    const next = nextSearchDateRange(specificDates, range, tappedDay);
    setSpecificDates(next);

    // Close calendar when both dates are selected (for round trips)
    if (tripType === '1' && next.from && next.to) {
      setIsCalendarOpen(false);
    } else if (tripType === '2' && next.from && !hadBoth) {
      // For one-way trips, close after selecting departure date
      setIsCalendarOpen(false);
    }
  }, [specificDates, tripType]);

  // Filter destinations for the map (no bounds filtering - show all valid destinations)
  const mapDestinations = useMemo(() => {
    if (!destinations || destinations.length === 0) return [];
    
    // Calculate minimum allowed date (21 days from today)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minBookingDate = new Date(today);
    minBookingDate.setDate(today.getDate() + 21);

    // Filter out destinations with N/A or missing prices, and dates < 21 days
    let filtered = destinations.filter(dest => {
      const price = dest.flight_price;
      // Remove if price is null, undefined, "N/A", or empty string
      if (price === null || price === undefined || price === 'N/A' || price === '' || isNaN(parseFloat(price))) {
        return false;
      }

      // Filter out destinations with departure dates less than 21 days away
      if (dest.start_date) {
        try {
          const startDateStr = dest.start_date.includes('T') 
            ? dest.start_date.split('T')[0] 
            : dest.start_date.split(' ')[0];
          const startDate = new Date(startDateStr);
          startDate.setHours(0, 0, 0, 0);
          
          if (startDate < minBookingDate) {
            return false;
          }
        } catch (error) {
          return false;
        }
      }

      return true;
    });

    // Apply filter type (cities vs destinations) - same as list view
    if (filterType === 'cities') {
      // Filter out national parks and similar destinations
      filtered = filtered.filter(dest => {
        const name = dest.name?.toLowerCase() || '';
        // Exclude national parks, state parks, and similar
        return !name.includes('national park') && 
               !name.includes('state park') && 
               !name.includes('national monument') &&
               !name.includes('national forest') &&
               !name.includes('wilderness area') &&
               !name.includes('recreation area');
      });
    }

    return filtered;
  }, [destinations, filterType]);

  // Filter destinations for the list view (includes bounds filtering)
  const filteredDestinations = useMemo(() => {
    // Calculate minimum allowed date (21 days from today)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minBookingDate = new Date(today);
    minBookingDate.setDate(today.getDate() + 21);

    // First, filter out destinations with N/A or missing prices
    let filtered = destinations.filter(dest => {
      // Filter by visible map bounds if available
      if (mapBounds && dest.gps_coordinates?.latitude && dest.gps_coordinates?.longitude) {
        const lat = dest.gps_coordinates.latitude;
        const lng = dest.gps_coordinates.longitude;
        // Check if destination is within visible bounds (with small padding for edge cases)
        if (lat < mapBounds.south - 2 || lat > mapBounds.north + 2 || 
            lng < mapBounds.west - 2 || lng > mapBounds.east + 2) {
          return false;
        }
      }
      const price = dest.flight_price;
      // Remove if price is null, undefined, "N/A", or empty string
      if (price === null || price === undefined || price === 'N/A' || price === '' || isNaN(parseFloat(price))) {
        return false;
      }

      // Filter out destinations with departure dates less than 21 days away
      if (dest.start_date) {
        try {
          const startDateStr = dest.start_date.includes('T') 
            ? dest.start_date.split('T')[0] 
            : dest.start_date.split(' ')[0];
          const startDate = new Date(startDateStr);
          startDate.setHours(0, 0, 0, 0);
          
          if (startDate < minBookingDate) {
            return false; // Skip destinations with dates too soon
          }
        } catch (error) {
          console.warn('Error parsing start_date:', dest.start_date, error);
          // If we can't parse the date, exclude it to be safe
          return false;
        }
      }

      return true;
    });

    // Then apply filter type (cities vs destinations)
    if (filterType === 'cities') {
      // Filter out national parks and similar destinations
      filtered = filtered.filter(dest => {
        const name = dest.name?.toLowerCase() || '';
        // Exclude national parks, state parks, and similar
        return !name.includes('national park') && 
               !name.includes('state park') && 
               !name.includes('national monument') &&
               !name.includes('national forest') &&
               !name.includes('wilderness area') &&
               !name.includes('recreation area');
      });
    }
    
    // Sort by lowest price (ascending)
    filtered.sort((a, b) => {
      const priceA = parseFloat(a.flight_price) || 0;
      const priceB = parseFloat(b.flight_price) || 0;
      return priceA - priceB;
    });
    
    return filtered;
  }, [destinations, filterType, mapBounds]);

  // Show error state if component error occurred
  if (componentError) {
    return (
      <div className="min-h-screen bg-brand-950 flex items-center justify-center p-4 -mt-[var(--app-header-height)] pt-[calc(var(--app-header-height)+1rem)]">
        <Card className={cn(CART_SECTION_CARD, 'border-red-500/30 bg-red-500/10 max-w-md')}>
          <CardContent className="p-6 text-center">
            <h2 className="text-xl font-light text-white mb-2">Error Loading Explore</h2>
            <p className="text-red-400 text-sm mb-4">{componentError}</p>
            <Button onClick={() => window.location.reload()} className={cn(BRAND_PRIMARY_BTN_CLASS, 'rounded-full')}>
              Reload Page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Wrap map in error boundary to prevent crashes
  return (
    <ErrorBoundary componentName="Explore">
      <>
        <PageSeo path="/explore" />
        
        <div className="min-h-screen bg-brand-950 text-white relative overflow-x-hidden -mt-[var(--app-header-height)] pt-[var(--app-header-height)]">
          {/* Full-viewport royal wash — covers Layout's black gradient-bg behind the header */}
          <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden>
            <div className="absolute inset-0 bg-gradient-to-b from-brand-950 via-[#050814] to-black" />
            <div className="absolute top-0 left-0 w-96 h-96 bg-brand-800/15 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-0 w-[32rem] h-[32rem] bg-brand-900/20 rounded-full blur-3xl" />
          </div>
          <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-8 py-6">
            {/* Header */}
            <div className="mb-6">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="mb-4 text-white/60 hover:text-white hover:bg-brand-900/40"
              >
                ← Back
              </Button>
              <h1 className="text-3xl md:text-4xl font-light text-white mb-2 tracking-tight">Explore Destinations</h1>
              <p className="text-white/65 text-sm">Discover amazing places with flexible travel dates</p>
            </div>

          {/* Search Form */}
          <div className={cn(REWARDS_SECTION_CARD, 'mb-6 overflow-hidden')}>
            <SectionBand
              icon={Plane}
              title="Search"
              subtitle="Departure, dates, and travelers"
            />
            <div className="p-5 sm:p-6 space-y-5">
              {/* Two-column layout: Departure fields on left, Trip Type/Passengers on right - 1:1 ratio, edge to edge on desktop */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-start">
                {/* Left Column: Departure and Date Fields */}
                <div className="space-y-4">
                  {/* Origin Input */}
                  <div>
                    <label className={exploreLabelClass}>Departure</label>
                    <AirportInput 
                      placeholder="From" 
                      value={origin} 
                      onChange={setOrigin} 
                      className={exploreFieldClass} 
                    />
                  </div>

                  {/* Date Mode Toggle */}
                  <div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setDateMode('flexible')}
                        className={exploreToggleClass(dateMode === 'flexible')}
                      >
                        Flexible dates
                      </button>
                      <button
                        type="button"
                        onClick={() => setDateMode('specific')}
                        className={exploreToggleClass(dateMode === 'specific')}
                      >
                        Specific dates
                      </button>
                    </div>
                  </div>

                  {/* Flexible Date Selector */}
                  {dateMode === 'flexible' && (
                    <FlexibleDateSelector
                      month={month}
                      travelDuration={travelDuration}
                      onMonthChange={setMonth}
                      onTravelDurationChange={setTravelDuration}
                    />
                  )}

                  {/* Specific Date Selector */}
                  {dateMode === 'specific' && (
                    <div>
                      <label className={exploreLabelClass}>Travel dates</label>
                      <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn('w-full justify-start text-left font-normal rounded-full', exploreFieldClass, 'hover:bg-brand-900/50')}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {specificDates.from ? (
                              tripType === '2' ? (
                                format(specificDates.from, 'MMM d')
                              ) : specificDates.to ? (
                                `${format(specificDates.from, 'MMM d')} - ${format(specificDates.to, 'MMM d')}`
                              ) : (
                                `${format(specificDates.from, 'MMM d')} - Select return`
                              )
                            ) : (
                              tripType === '2' ? 'Select date' : 'Select dates'
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className={cn(CART_SECTION_CARD, 'w-auto p-0')} align="start">
                          <CalendarComponent
                            mode={tripType === '2' ? 'single' : 'range'}
                            selected={tripType === '2' ? specificDates.from : { from: specificDates.from, to: specificDates.to }}
                            onSelect={(date, tappedDay) => {
                              if (tripType === '2') {
                                // One-way: just set the departure date
                                const validDate = date instanceof Date && !isNaN(date.getTime()) ? date : undefined;
                                setSpecificDates({ from: validDate, to: undefined });
                                if (validDate) setIsCalendarOpen(false);
                              } else {
                                // Round trip: use range handler
                                handleDateSelect(date, tappedDay);
                              }
                            }}
                            numberOfMonths={2}
                            disabled={(date) => date < new Date()}
                            className="bg-brand-950 text-white"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </div>

                {/* Right Column: Trip Type, Passengers, Cabin Class */}
                <div className="space-y-4">
                  {/* Trip Type Selector - aligned with Departure field */}
                  <div>
                    <label className={exploreLabelClass}>Trip type</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          console.log('[Explore] Round trip button clicked, setting tripType to "1"');
                          setTripType('1');
                        }}
                        className={exploreToggleClass(tripType === '1')}
                      >
                        Round trip
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          console.log('[Explore] One way button clicked, setting tripType to "2"');
                          setTripType('2');
                          // Clear return date for one-way trips
                          if (specificDates.to) {
                            setSpecificDates({ from: specificDates.from, to: undefined });
                          }
                        }}
                        className={exploreToggleClass(tripType === '2')}
                      >
                        One way
                      </button>
                    </div>
                  </div>

                  {/* Passengers and Cabin Class */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={exploreLabelClass}>Passengers</label>
                      <PassengerSelector
                        passengers={passengers}
                        onChange={setPassengers}
                        onOpenChange={onTravelersOpenChange}
                        className={exploreFieldClass}
                      />
                    </div>
                    <div>
                      <label className={exploreLabelClass}>Cabin class</label>
                      <CabinClassSelector
                        value={cabinClass}
                        onChange={setCabinClass}
                        className={exploreFieldClass}
                      />
                    </div>
                  </div>
                </div>
              </div>

                {/* Search Button */}
                <Button
                  onClick={handleSearch}
                  disabled={isLoading || !origin}
                  className={cn('w-full h-11 rounded-full mt-1', BRAND_PRIMARY_BTN_CLASS)}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Plane className="w-4 h-4 mr-2" />
                      Explore
                    </>
                  )}
                </Button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <Card className={cn(CART_SECTION_CARD, 'border-red-500/30 bg-red-500/10 mb-6')}>
              <CardContent className="p-4">
                <p className="text-red-400 text-sm">{error}</p>
              </CardContent>
            </Card>
          )}

          {/* Results: Map and List */}
          {(isLoading || destinations.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Map */}
              <div className="lg:col-span-3">
                <div
                  className={cn(
                    REWARDS_SECTION_CARD,
                    'flex flex-col lg:sticky lg:top-6 h-[600px] md:h-[700px] lg:h-[calc(100vh-140px)]',
                  )}
                >
                  <SectionBand icon={MapPin} title="Map" subtitle="Tap a pin to preview" />
                  <div className="p-2 flex-1 min-h-0">
                    <div className="w-full h-full rounded-xl overflow-hidden border border-brand-800/45 ring-1 ring-brand-800/25 bg-brand-950">
                      {isLoading && destinations.length === 0 ? (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-brand-900/25">
                          <Loader2 className="w-8 h-8 text-brand-400 animate-spin" />
                          <p className="text-sm text-white/50 font-normal">Finding destinations…</p>
                        </div>
                      ) : (
                        <ErrorBoundary componentName="ExploreMap">
                          <ExploreMap
                            destinations={mapDestinations}
                            origin={origin}
                            center={undefined}
                            zoom={undefined}
                            onDestinationClick={handleDestinationClick}
                            selectedDestination={selectedDestination}
                            onRegionChange={handleRegionChange}
                            onBoundsChange={setMapBounds}
                            searchParams={{
                              tripType,
                              cabinClass,
                              passengers,
                              dateMode,
                              specificDates,
                              month,
                              travelDuration,
                            }}
                            skipAutoCenter={true}
                          />
                        </ErrorBoundary>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Destinations list */}
              <div className="lg:col-span-2">
                <div className={cn(REWARDS_SECTION_CARD, 'flex flex-col lg:max-h-[calc(100vh-140px)] overflow-hidden')}>
                  <SectionBand
                    icon={Plane}
                    title="Destinations"
                    subtitle={
                      isLoading && destinations.length === 0
                        ? 'Searching…'
                        : `${filteredDestinations.length} found`
                    }
                    rightSlot={
                      !isLoading && destinations.length > 0 ? (
                        <div className="relative" ref={filterMenuRef}>
                          <button
                            onClick={() => setShowFilterMenu(!showFilterMenu)}
                            className={cn(
                              'flex items-center gap-2 px-3 py-1.5 rounded-full transition-all border text-xs',
                              DASHBOARD_TAB_INACTIVE,
                              showFilterMenu && 'border-brand-700/50 bg-brand-900/45 text-white/85',
                            )}
                          >
                            <Filter className="w-3.5 h-3.5" />
                            <span className="capitalize">{filterType}</span>
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilterMenu ? 'rotate-180' : ''}`} />
                          </button>

                          {showFilterMenu && (
                            <div className={cn(CART_SECTION_CARD, 'absolute right-0 mt-2 w-40 shadow-lg z-10 overflow-hidden')}>
                              <button
                                onClick={() => {
                                  setFilterType('cities');
                                  setShowFilterMenu(false);
                                }}
                                className={cn(
                                  'w-full text-left px-4 py-2 text-sm transition-colors border-b border-brand-800/30',
                                  filterType === 'cities'
                                    ? 'bg-brand-800/35 text-white'
                                    : 'text-white/60 hover:bg-brand-900/30 hover:text-white',
                                )}
                              >
                                Cities
                              </button>
                              <button
                                onClick={() => {
                                  setFilterType('destinations');
                                  setShowFilterMenu(false);
                                }}
                                className={cn(
                                  'w-full text-left px-4 py-2 text-sm transition-colors',
                                  filterType === 'destinations'
                                    ? 'bg-brand-800/35 text-white'
                                    : 'text-white/60 hover:bg-brand-900/30 hover:text-white',
                                )}
                              >
                                Destinations
                              </button>
                            </div>
                          )}
                        </div>
                      ) : null
                    }
                  />

                  <div className="flex justify-end px-4 pt-3 -mb-1">
                    <AllInPriceToggle checked={showAllInPrices} onCheckedChange={setShowAllInPrices} />
                  </div>

                  <div className={cn('flex-1 overflow-y-auto', BRAND_LIST_DIVIDER)}>
                    {isLoading && destinations.length === 0 ? (
                      Array.from({ length: 5 }).map((_, index) => (
                        <div key={index} className="p-4 animate-pulse">
                          <div className="flex items-start gap-4">
                            <div className="w-[72px] h-[72px] rounded-xl bg-brand-800/30 shrink-0" />
                            <div className="flex-1 space-y-2">
                              <div className="h-4 w-2/3 rounded bg-brand-800/30" />
                              <div className="h-3 w-1/3 rounded bg-brand-800/20" />
                              <div className="h-3 w-1/2 rounded bg-brand-800/20" />
                            </div>
                          </div>
                        </div>
                      ))
                    ) : filteredDestinations.length === 0 ? (
                      <div className="p-10 text-center">
                        <Filter className="w-10 h-10 text-white/35 mx-auto mb-3" />
                        <h3 className="text-base font-normal text-white mb-1">No {filterType} found</h3>
                        <p className="text-white/60 text-sm font-normal">Try a different filter</p>
                      </div>
                    ) : (
                      filteredDestinations.map((destination, index) => (
                        <motion.button
                          key={destination.destination_id || index}
                          type="button"
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.03 }}
                          onClick={() => handleDestinationClick(destination)}
                          className={cn(
                            'w-full text-left p-4 sm:px-5 transition-colors hover:bg-brand-900/30 border-l-2 border-transparent',
                            selectedDestination?.destination_id === destination.destination_id &&
                              'bg-brand-800/20 hover:bg-brand-800/25 border-l-brand-500/70',
                          )}
                        >
                          <div className="flex items-start gap-3.5">
                            {destination.thumbnail ? (
                              <img
                                src={destination.thumbnail}
                                alt=""
                                className="w-[72px] h-[72px] rounded-xl object-cover flex-shrink-0 ring-1 ring-brand-800/40"
                              />
                            ) : (
                              <div className="w-[72px] h-[72px] rounded-xl bg-brand-900/40 ring-1 ring-brand-800/40 flex items-center justify-center shrink-0">
                                <MapPin className="w-5 h-5 text-white/40" />
                              </div>
                            )}

                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <h3 className="text-[15px] font-medium text-white truncate leading-snug">
                                    {destination.name}
                                  </h3>
                                  <p className="text-xs text-white/50 mt-0.5">{destination.country}</p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  {destination.flight_price && !isNaN(parseFloat(destination.flight_price)) ? (
                                    <>
                                      <p className="text-base font-medium text-white tabular-nums">
                                        ${Math.round(estimatedDepositFromFare(parseFloat(destination.flight_price), exploreLeadDays(destination.start_date), installmentsCheckoutProvider === 'defera_capital')).toLocaleString('en-US')} today
                                      </p>
                                      <p key={showAllInPrices ? 'allin' : 'fare'} className="text-xs text-white/65 tabular-nums transform-gpu">
                                        {showAllInPrices
                                          ? `$${Math.ceil(allInTotalFromFare(parseFloat(destination.flight_price), installmentsCheckoutProvider === 'defera_capital')).toLocaleString('en-US')} total`
                                          : `$${destination.flight_price} fare`}
                                      </p>
                                    </>
                                  ) : (
                                    <p className="text-base font-medium text-white">N/A</p>
                                  )}
                                </div>
                              </div>

                              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-white/50 mt-2">
                                {destination.airline && (
                                  <span className="inline-flex items-center gap-1">
                                    <Plane className="w-3 h-3" />
                                    {destination.airline}
                                  </span>
                                )}
                                {destination.flight_duration && (
                                  <span className="inline-flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {formatDuration(destination.flight_duration)}
                                  </span>
                                )}
                                {destination.number_of_stops !== undefined && (
                                  <span>
                                    {destination.number_of_stops === 0
                                      ? 'Nonstop'
                                      : `${destination.number_of_stops} stop${destination.number_of_stops !== 1 ? 's' : ''}`}
                                  </span>
                                )}
                              </div>

                              {destination.start_date && (
                                <div className="flex items-center gap-1 text-[11px] text-white/45 mt-1.5">
                                  <Calendar className="w-3 h-3" />
                                  {format(parseISO(destination.start_date), 'MMM d')}
                                  {destination.end_date && ` – ${format(parseISO(destination.end_date), 'MMM d')}`}
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isLoading && destinations.length === 0 && !error && (
            <Card className={CART_SECTION_CARD}>
              <CardContent className="p-12 text-center bg-transparent">
                <MapPin className="w-12 h-12 text-white/40 mx-auto mb-4" />
                <h3 className="text-xl font-light text-white mb-2">Start Exploring</h3>
                <p className="text-white/60 text-sm">
                  Enter a departure airport and click Explore to discover destinations
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Destination Preview Overlay */}
        {showPreview && selectedDestination && (
          <DestinationPreviewOverlay
            destination={selectedDestination}
            origin={origin}
            showAllInPrices={showAllInPrices}
            deferaCapitalTier={installmentsCheckoutProvider === 'defera_capital'}
            onClose={() => {
              setShowPreview(false);
              setSelectedDestination(null);
            }}
            onViewFlights={() => handleViewFlights(selectedDestination)}
          />
        )}
      </div>
      </>
    </ErrorBoundary>
  );
}
